import React, { useEffect } from "react";
import { UseSaveTour } from "../../../api/calls/Queries";
import MapList from "./MapList";
import MapDetails from "./MapDetails";
import { useQueryClient } from "@tanstack/react-query";
import { TourData } from "../../../Types";
import {
  useCurrentSceneStore,
  useMapEditorStore,
  useTourDataStore,
} from "../../../api/Zustand";
import { getLanguageFile } from "../../common/GetLanguageFile";

/* eslint import/no-webpack-loader-syntax: off */
//@ts-expect-error
import css from "!raw-loader!../AssetList/assetlist.css";

const MapEditor = (props: { tourID: number }) => {
  const mapEditor = useMapEditorStore();

  const { tourData } = useTourDataStore();

  const queryClient = useQueryClient();
  const { currentSceneID } = useCurrentSceneStore();

  const [page, setPage] = React.useState<1 | 2>(1);
  const [selected, setSelected] = React.useState<number | undefined>(undefined);

  const { mutateAsync: saveTour, isPending } = UseSaveTour();

  useEffect(() => {
    if (mapEditor.onPage) {
      setPage(mapEditor.onPage);
      setSelected(mapEditor.currentMap);
    }
  }, [mapEditor.onPage, mapEditor.currentMap]);

  const onMapDelete = React.useCallback(async () => {
    setPage(1);
    return queryClient.setQueryData(["GetTour", props.tourID], {
      ...tourData,
      maps: tourData?.maps?.filter((gr) => gr.id !== selected),
      scenes: tourData?.scenes.map((sc) =>
        sc.map === mapEditor.currentMap
          ? {
              ...sc,
              map: undefined,
            }
          : sc
      ),
    });
  }, [props.tourID, tourData, selected, queryClient, mapEditor.currentMap]);

  const setMapInScene = React.useCallback(async () => {
    const newTourData = await queryClient.setQueryData(
      ["GetTour", props.tourID],
      {
        ...tourData,
        scenes: tourData?.scenes.map((sc) =>
          sc.id === currentSceneID ? { ...sc, map: selected } : sc
        ),
      }
    );
    saveTour(newTourData as TourData, {
      onSuccess: () => {
        setPage(1);
        mapEditor.setClosed();
      },
    });
  }, [
    currentSceneID,
    mapEditor,
    props.tourID,
    queryClient,
    saveTour,
    selected,
    tourData,
  ]);

  const hasMaps = React.useMemo(() => {
    const maps = tourData?.maps;

    if (maps) {
      return maps?.length > 0;
    } else return false;
  }, [tourData?.maps]);

  const selectedMap = React.useMemo(() => {
    if (selected) {
      return tourData?.maps?.find((one) => one.id === selected);
    }
  }, [selected, tourData?.maps]);

  if (mapEditor.isOpen && tourData)
    return (
      <>
        <style>{css}</style>
        <div
          style={wrapperStyle}
          onClick={() => {
            if (!isPending) mapEditor.setClosed();
          }}
        >
          <div
            style={panelStyle}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <header
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2
                style={{ margin: 0, userSelect: "none" }}
                onClick={() => {
                  setPage(1);
                  // mapEditor.setOpen()
                  // openMap({ ...map, fromScene: false });
                }}
              >
                {page === 2
                  ? getLanguageFile("overlapping_panel.back")
                  : getLanguageFile("overlapping_panel.maps")}
              </h2>
              <button
                id="assetListClose"
                style={{ color: " #222" }}
                onClick={() => {
                  if (!isPending) mapEditor.setClosed();
                }}
              >
                {getLanguageFile("overlapping_panel.close")}
              </button>
            </header>

            <div
              style={{
                width: "100%",
                height: "100%",
                overflowX: "auto",
              }}
            >
              {page === 1 ? (
                <MapList
                  tourData={tourData}
                  tourID={props.tourID}
                  setPage={setPage}
                  setSelected={setSelected}
                  selected={selected}
                />
              ) : (
                <MapDetails
                  selected={selected}
                  tourID={props.tourID}
                  tourData={tourData}
                />
              )}
            </div>
            <div
              style={{
                height: 50,
                width: "100%",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {page === 2 && (
                  <button
                    style={{ fontWeight: 700 }}
                    id="map-delete"
                    onClick={() => onMapDelete()}
                  >
                    {getLanguageFile("overlapping_panel.delete_map")}{" "}
                  </button>
                )}
                <button
                  id="file-upload"
                  disabled={isPending || !selectedMap?.assetId}
                  onClick={() =>
                    saveTour(tourData, {
                      onSuccess: () => {
                        if (page === 1) mapEditor.setClosed();
                        else {
                          if (mapEditor.fromScene) mapEditor.setClosed();

                          setPage(1);
                        }
                      },
                    })
                  }
                >
                  {isPending
                    ? getLanguageFile("generics.saving")
                    : getLanguageFile("generics.save")}
                </button>

                {!isPending && !mapEditor.fromScene && (
                  <button
                    id="file-upload"
                    disabled={!selectedMap?.assetId || !hasMaps}
                    onClick={() => setMapInScene()}
                  >
                    {isPending
                      ? getLanguageFile("generics.saving")
                      : getLanguageFile("overlapping_panel.save_and_use")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
};

export default MapEditor;

const wrapperStyle: React.CSSProperties = {
  background: "#0008",
  position: "absolute",
  zIndex: "1100",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const panelStyle: React.CSSProperties = {
  // background: "#E8F0FF",
  background: "#fff",
  width: "90%",
  height: "90%",
  display: "flex",
  flexDirection: "column",
  padding: "1rem",
  boxSizing: "border-box",
  zIndex: 3,
};
